import { Bandage as BandageIcon, BriefcaseMedical as BriefcaseMedicalIcon, FlaskConical as FlaskConicalIcon, Heart as HeartIcon, Hospital as HospitalIcon, ScanLine as ScanLineIcon, Siren as SirenIcon, Stethoscope as StethoscopeIcon, UserRound as UserRoundIcon, Video as VideoIcon, Zap as ZapIcon } from "@tamagui/lucide-icons";
import { InsuranceCareCategory } from "@medbillai/generated-graphql-types";
import { Avatar, type AvatarProps, Badge, type BadgeProps, type IconComponent } from "@medbillai/ui";
export const careCategoryLabel: Record<InsuranceCareCategory, string> = {
  [InsuranceCareCategory.PRIMARY_CARE]: "Primary care",
  [InsuranceCareCategory.SPECIALIST]: "Specialist",
  [InsuranceCareCategory.URGENT_CARE]: "Urgent care",
  [InsuranceCareCategory.EMERGENCY_ROOM]: "ER visit",
  [InsuranceCareCategory.PREVENTIVE_CARE]: "Preventive care",
  [InsuranceCareCategory.MENTAL_HEALTH]: "Mental health",
  [InsuranceCareCategory.SUBSTANCE_USE]: "Substance use care",
  [InsuranceCareCategory.TELEHEALTH]: "Telehealth",
  [InsuranceCareCategory.LAB_WORK]: "Lab work",
  [InsuranceCareCategory.IMAGING]: "Imaging",
  [InsuranceCareCategory.INPATIENT]: "Hospital stay",
  [InsuranceCareCategory.OUTPATIENT_SURGERY]: "Outpatient surgery",
  [InsuranceCareCategory.MATERNITY]: "Maternity",
  [InsuranceCareCategory.PHYSICAL_THERAPY]: "Physical therapy",
  [InsuranceCareCategory.OCCUPATIONAL_THERAPY]: "Occupational therapy",
  [InsuranceCareCategory.SPEECH_THERAPY]: "Speech therapy",
  [InsuranceCareCategory.CHIROPRACTIC]: "Chiropractic",
  [InsuranceCareCategory.SKILLED_NURSING]: "Skilled nursing",
  [InsuranceCareCategory.HOME_HEALTH]: "Home health",
  [InsuranceCareCategory.DURABLE_MEDICAL_EQUIPMENT]: "Medical equipment",
  [InsuranceCareCategory.VISION_CARE]: "Vision care",
  [InsuranceCareCategory.DENTAL_CARE]: "Dental care",
  [InsuranceCareCategory.GENERIC_RX]: "Generic drugs",
  [InsuranceCareCategory.PREFERRED_BRAND_RX]: "Preferred brand drugs",
  [InsuranceCareCategory.NON_PREFERRED_BRAND_RX]: "Non-preferred brand drugs",
  [InsuranceCareCategory.SPECIALTY_RX]: "Specialty drugs",
  [InsuranceCareCategory.UNRECOGNIZED]: "Other care"
};
type CategoryLook = {
  icon: IconComponent;
  background: "$bgCategoryGreen" | "$bgCategoryRed" | "$bgCategoryBlue" | "$bgCategoryTeal" | "$bgCategoryPurple" | "$bgCategoryMagenta" | "$bgCategoryOrange" | "$bgCategorySand" | "$surface0";
  color: "$iconCategoryGreen" | "$iconCategoryRed" | "$iconCategoryBlue" | "$iconCategoryTeal" | "$iconCategoryPurple" | "$iconCategoryMagenta" | "$iconCategoryOrange" | "$iconCategorySand" | "$iconSubtle";
  text: "$textCategoryGreen" | "$textCategoryRed" | "$textCategoryBlue" | "$textCategoryTeal" | "$textCategoryPurple" | "$textCategoryMagenta" | "$textCategoryOrange" | "$textCategorySand" | "$textDefault";
};
const DEFAULT_LOOK: CategoryLook = {
  icon: BriefcaseMedicalIcon,
  background: "$surface0",
  color: "$iconSubtle",
  // `$textSubtle` on `$surface0` is under AA for badge text.
  text: "$textDefault"
};
const LOOKS: Partial<Record<InsuranceCareCategory, CategoryLook>> = {
  [InsuranceCareCategory.PRIMARY_CARE]: {
    icon: StethoscopeIcon,
    background: "$bgCategoryGreen",
    color: "$iconCategoryGreen",
    text: "$textCategoryGreen"
  },
  [InsuranceCareCategory.SPECIALIST]: {
    icon: UserRoundIcon,
    background: "$bgCategoryTeal",
    color: "$iconCategoryTeal",
    text: "$textCategoryTeal"
  },
  [InsuranceCareCategory.URGENT_CARE]: {
    icon: ZapIcon,
    background: "$bgCategoryRed",
    color: "$iconCategoryRed",
    text: "$textCategoryRed"
  },
  [InsuranceCareCategory.EMERGENCY_ROOM]: {
    icon: SirenIcon,
    background: "$bgCategoryRed",
    color: "$iconCategoryRed",
    text: "$textCategoryRed"
  },
  [InsuranceCareCategory.INPATIENT]: {
    ...DEFAULT_LOOK,
    icon: HospitalIcon
  },
  [InsuranceCareCategory.OUTPATIENT_SURGERY]: {
    icon: BandageIcon,
    background: "$bgCategorySand",
    color: "$iconCategorySand",
    text: "$textCategorySand"
  },
  [InsuranceCareCategory.MENTAL_HEALTH]: {
    icon: HeartIcon,
    background: "$bgCategoryMagenta",
    color: "$iconCategoryMagenta",
    text: "$textCategoryMagenta"
  },
  [InsuranceCareCategory.TELEHEALTH]: {
    icon: VideoIcon,
    background: "$bgCategoryBlue",
    color: "$iconCategoryBlue",
    text: "$textCategoryBlue"
  },
  [InsuranceCareCategory.LAB_WORK]: {
    icon: FlaskConicalIcon,
    background: "$bgCategoryPurple",
    color: "$iconCategoryPurple",
    text: "$textCategoryPurple"
  },
  [InsuranceCareCategory.IMAGING]: {
    icon: ScanLineIcon,
    background: "$bgCategoryOrange",
    color: "$iconCategoryOrange",
    text: "$textCategoryOrange"
  }
};
export const careCategoryLook = (category: InsuranceCareCategory) => LOOKS[category] ?? DEFAULT_LOOK;

/** The category's icon and tint, for an Avatar or an IconPill. */
export const careCategoryAvatarProps = (category: InsuranceCareCategory) => {
  const {
    icon,
    background,
    color
  } = careCategoryLook(category);
  return {
    icon,
    iconColor: color,
    backgroundColor: background
  };
};
export function CareCategoryAvatar({
  category,
  ...rest
}: {
  category: InsuranceCareCategory;
} & Omit<AvatarProps, "icon" | "iconColor" | "backgroundColor" | "status">) {
  return <Avatar size="small" aria-hidden {...rest} {...careCategoryAvatarProps(category)} data-sentry-element="Avatar" data-sentry-component="CareCategoryAvatar" data-sentry-source-file="careCategories.tsx" />;
}
export function CareCategoryBadge({
  category,
  ...rest
}: {
  category: InsuranceCareCategory;
} & Omit<BadgeProps, "children" | "bordered" | "iconLeft" | "iconColor" | "textColor" | "backgroundColor" | "status">) {
  const {
    icon,
    background,
    color,
    text
  } = careCategoryLook(category);
  return <Badge bordered={false} {...rest} iconLeft={icon} iconColor={color} textColor={text} backgroundColor={background} data-sentry-element="Badge" data-sentry-component="CareCategoryBadge" data-sentry-source-file="careCategories.tsx">
      {careCategoryLabel[category]}
    </Badge>;
}